import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { isWibNight } from "@/lib/datetime";

const ThemeContext = createContext(null);

// Preferensi tema disimpan PER PERANGKAT (localStorage), bukan per akun --
// sengaja tidak ikut disinkronkan lewat backend/API.
const STORAGE_KEY = "simonev_theme_mode";
// Cek ulang waktu WIB tiap 1 menit, supaya kalau aplikasi dibiarkan terbuka
// melewati jam 06:00/18:00 WIB, tema ikut berubah otomatis tanpa reload.
const CHECK_INTERVAL_MS = 60 * 1000;
// Mode yang valid:
//   "auto"   -- mengikuti jam WIB (gelap 18.00-05.59), default bila belum pernah dipilih.
//   "system" -- mengikuti pengaturan perangkat (prefers-color-scheme), berubah otomatis.
//   "light"/"dark" -- override manual.
const MODES = ["auto", "system", "light", "dark"];
const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

function readStoredMode() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return MODES.includes(v) ? v : "auto";
    } catch {
        // localStorage tidak bisa diakses (mis. diblokir browser) -> ikuti sistem.
        return "system";
    }
}

function systemPrefersDark() {
    try {
        return window.matchMedia(SYSTEM_DARK_QUERY).matches;
    } catch {
        return false;
    }
}

function computeEffectiveTheme(mode) {
    if (mode === "light" || mode === "dark") return mode;
    if (mode === "system") return systemPrefersDark() ? "dark" : "light";
    return isWibNight() ? "dark" : "light"; // mode === "auto"
}

function applyThemeToDocument(theme) {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.style.colorScheme = theme;
}

export function ThemeProvider({ children }) {
    const [mode, setModeState] = useState(readStoredMode);
    const [effectiveTheme, setEffectiveTheme] = useState(() => computeEffectiveTheme(mode));

    // Hitung ulang tema efektif & terapkan ke <html> hanya bila memang berubah.
    const refresh = useCallback(() => {
        const next = computeEffectiveTheme(mode);
        setEffectiveTheme((prev) => {
            if (prev === next) return prev;
            applyThemeToDocument(next);
            return next;
        });
    }, [mode]);

    // Terapkan tiap kali mode (override manual) berubah.
    useEffect(() => {
        const next = computeEffectiveTheme(mode);
        setEffectiveTheme(next);
        applyThemeToDocument(next);
    }, [mode]);

    // Poll waktu WIB tiap menit -- hanya berefek saat mode === "auto"
    // (computeEffectiveTheme mengembalikan nilai tetap utk mode lain).
    useEffect(() => {
        if (mode !== "auto") return undefined;
        const id = setInterval(refresh, CHECK_INTERVAL_MS);
        return () => clearInterval(id);
    }, [mode, refresh]);

    // Mode "system": ikut berubah otomatis saat pengaturan tema perangkat diganti.
    useEffect(() => {
        if (mode !== "system") return undefined;
        let mql;
        try {
            mql = window.matchMedia(SYSTEM_DARK_QUERY);
        } catch {
            return undefined;
        }
        mql.addEventListener?.("change", refresh);
        return () => mql.removeEventListener?.("change", refresh);
    }, [mode, refresh]);

    const setMode = useCallback((next) => {
        if (!MODES.includes(next)) return;
        setModeState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    }, []);

    return (
        <ThemeContext.Provider value={{ mode, effectiveTheme, setMode }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
